import { apiClient } from '@/lib/api-client';
import type { ChargePointFilterParams } from '@/features/filters/types/filter.types';

/**
 * Per-country charge metrics from GET /api/v1/charge_points/by_country.
 * Countries with no matching charge points are omitted from the response.
 */
export interface CountryMetrics {
  /** ISO Alpha-2, e.g. "DK" — keyed against the GeoJSON `iso_a2` property */
  code_2: string;
  code_3: string | null;
  name: string | null;
  /** Sites + standalone charge points (map pins) */
  locations: number;
  /** True total charge points */
  charge_points: number;
  /** Currently available charge points */
  available: number;
  /** Distinct operators present */
  operators: number;
}

interface ByCountryResponse {
  countries?: CountryMetrics[];
}

/**
 * Only the filters the by_country endpoint accepts. Availability is a returned
 * metric there, not a filter, so we deliberately drop the availability params.
 */
function pickByCountryParams(
  filters: ChargePointFilterParams
): Record<string, unknown> {
  const params = {
    operator_ids: filters.operator_ids,
    team_ids: filters.team_ids,
    promotion_ids: filters.promotion_ids,
    min_kw: filters.min_kw,
    max_kw: filters.max_kw,
  };

  // Drop null/undefined/empty so we don't send blank query params.
  return Object.fromEntries(
    Object.entries(params).filter(([, v]) => v != null && v !== '')
  );
}

export const countryCountsApi = {
  async getByCountry(
    filters: ChargePointFilterParams
  ): Promise<CountryMetrics[]> {
    const response = await apiClient.get<ByCountryResponse>(
      '/api/v1/charge_points/by_country',
      pickByCountryParams(filters)
    );

    return response.countries ?? [];
  },
};
